import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  addEdge,
  Controls,
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
} from 'react-flow-renderer';
import { createPortal } from 'react-dom';
import { AppButton, AppTextField } from 'components';
import CustomNode from 'components/CustomMindMap/CustomNode';
import useManageMindMap from 'hooks/API/useManageMindMap';
import useGetMindMap from 'hooks/API/GET/useGetMindMap';
import { useParams } from 'react-router-dom';
import Joi from 'joi';
import { notifier } from 'components/Toaster';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import Editor from 'components/CKEditor/Editor';

const actionsConfig = {
  open: false,
  data: {},
  Header: null,
  Body: null,
  isDeleteOperation: false,
};

const nodeTypes = { custom: CustomNode };

// Adjust node spacing
const HORIZONTAL_GAP = 300;
const VERTICAL_GAP = 200;

export default function MindMapFlow() {
  const { courseID, chapterID } = useParams();

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});

  // Position for the next node to be created. Computed either from the
  // current viewport center (plain "Add Node") or from where a "+" drag was
  // dropped on the canvas — never a random spot.
  const [pendingPosition, setPendingPosition] = useState(null);
  const reactFlowWrapperRef = useRef(null);
  const reactFlowInstanceRef = useRef(null);

  // Drag-from-node placement — dragging from a node's "+" connector and
  // dropping on empty canvas creates AND links the new node in one action.
  const [pendingParentId, setPendingParentId] = useState(null);
  const connectingNodeRef = useRef(null);

  // True when the user explicitly chose the drop spot by dragging from a "+"
  // connector; false when they just clicked "+" and we're auto-placing the
  // node. Auto-placed children get re-centered under their parent so the
  // tree reads as a proper hierarchy; explicitly-dragged placements are left
  // exactly where the user dropped them.
  const [pendingPositionIsManual, setPendingPositionIsManual] = useState(false);

  const [formData, setFormData] = useState({ title: '', text: '' });
  const [actions, setActions] = useState({ ...actionsConfig });

  const { data: mindMapData } = useGetMindMap({
    courseId: courseID,
    chapterId: chapterID,
    enabled: true,
  });

  const { mutate: saveMindMapMutation } = useManageMindMap({
    courseId: courseID,
    chapterId: chapterID,
  });

  // Node Edit/Delete Handlers
  const handleEditNodeById = useCallback(
    (id) => {
      const node = nodes.find((n) => n.id === id);
      if (!node) return;

      setSelectedNode(node);
      setFormData({
        title: node.data.title || '',
        text: node.data.description || '',
      });
      setIsEditMode(true);
      setPendingPosition(null);
      setPendingParentId(null);
      setPendingPositionIsManual(false);
      setIsModalOpen(true);
    },
    [nodes]
  );

  const handleDeleteConfirm = () => {
    const { id, edgeId } = actions?.data || {};

    if (edgeId) {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      notifier.success('Connection removed successfully');
      setActions({ ...actionsConfig });
      return;
    }

    if (!id) return;

    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));

    notifier.success('Node deleted successfully');

    setActions({ ...actionsConfig });
  };

  // Modal Add/Update
  const handleAddNode = () => {
    const { error } = mindMapSchema.validate({ title: formData.title }, { abortEarly: false });

    if (error) {
      const formattedErrors = {};
      error.details.forEach((err) => {
        formattedErrors[err.path[0]] = err.message;
      });
      setErrors(formattedErrors);
      return;
    }

    setErrors({});

    if (isEditMode && selectedNode) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === selectedNode.id
            ? {
                ...n,
                data: {
                  ...n.data,
                  title: formData.title,
                  description: formData.text,
                },
              }
            : n
        )
      );
      notifier.success('Node updated successfully');
    } else {
      const id = Date.now().toString();
      // Use the computed spot (viewport center, or a "+" drag drop point);
      // fall back to a random position only in an unexpected edge case.
      let position = pendingPosition || { x: Math.random() * 400, y: Math.random() * 400 };

      // When the node was auto-placed (clicked "+" rather than dragged), lay
      // its whole sibling row out evenly centered under the parent, so the
      // parent's connector sits above the middle of its children instead of
      // above the first one — that's what makes the tree read as a proper
      // hierarchy instead of a lopsided list.
      let siblingXUpdates = null;
      if (pendingParentId && !pendingPositionIsManual) {
        const parentNode = nodes.find((n) => n.id === pendingParentId);
        if (parentNode) {
          const siblingIds = edges.filter((e) => e.source === pendingParentId).map((e) => e.target);
          const totalCount = siblingIds.length + 1;
          const y = parentNode.position.y + VERTICAL_GAP;
          const startOffset = -((totalCount - 1) / 2) * HORIZONTAL_GAP;
          const xs = Array.from(
            { length: totalCount },
            (_, i) => parentNode.position.x + startOffset + i * HORIZONTAL_GAP
          );

          siblingXUpdates = {};
          siblingIds.forEach((sibId, i) => {
            siblingXUpdates[sibId] = xs[i];
          });
          position = { x: xs[totalCount - 1], y };
        }
      }

      const newNode = {
        id,
        type: 'custom',
        position,
        data: {
          title: formData.title,
          description: formData.text,
        },
      };

      setNodes((nds) => {
        const withRecenteredSiblings = siblingXUpdates
          ? nds.map((n) =>
              siblingXUpdates[n.id] !== undefined
                ? { ...n, position: { ...n.position, x: siblingXUpdates[n.id] } }
                : n
            )
          : nds;
        return [...withRecenteredSiblings, newNode];
      });

      // If this node was created via the "+" connector (click or drag),
      // link the two automatically.
      if (pendingParentId) {
        setEdges((eds) =>
          addEdge(
            {
              id: `${pendingParentId}-${id}`,
              source: pendingParentId,
              target: id,
              type: 'smoothstep',
            },
            eds
          )
        );
      }

      notifier.success('Node added successfully');
    }

    setIsModalOpen(false);
    setIsEditMode(false);
    setSelectedNode(null);
    setFormData({ title: '', text: '' });
    setPendingPosition(null);
    setPendingParentId(null);
    setPendingPositionIsManual(false);
  };

  // React Flow Handlers
  const onNodesChange = (changes) => setNodes((nds) => applyNodeChanges(changes, nds));
  const onEdgesChange = (changes) => setEdges((eds) => applyEdgeChanges(changes, eds));
  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), []);

  // Compute the flow-space coordinate at the center of the currently visible
  // canvas, accounting for pan/zoom. Used as the default spot for a plain
  // "Add Node" click so new nodes always land somewhere sensible and visible
  // rather than at a random position.
  const getViewportCenter = () => {
    if (!reactFlowInstanceRef.current || !reactFlowWrapperRef.current) return null;
    const bounds = reactFlowWrapperRef.current.getBoundingClientRect();
    return reactFlowInstanceRef.current.project({ x: bounds.width / 2, y: bounds.height / 2 });
  };

  const openAddNodeModal = () => {
    setIsEditMode(false);
    setSelectedNode(null);
    setFormData({ title: '', text: '' });
    setErrors({});
    setPendingParentId(null);
    setPendingPositionIsManual(false);
    setPendingPosition(getViewportCenter() || { x: Math.random() * 400, y: Math.random() * 400 });
    setIsModalOpen(true);
  };

  // Click the "+" handle on a node to add a connected child node right away
  // (no drag needed). This just picks a reasonable preview spot just below
  // the parent — the real, hierarchy-aware position (centered evenly among
  // all siblings) is computed in handleAddNode once the node is actually
  // created, since that's when we know the final sibling count.
  const openAddChildModal = useCallback(
    (parentId) => {
      const parentNode = nodes.find((n) => n.id === parentId);
      const defaultPosition = parentNode
        ? { x: parentNode.position.x, y: parentNode.position.y + VERTICAL_GAP }
        : getViewportCenter() || { x: Math.random() * 400, y: Math.random() * 400 };

      setIsEditMode(false);
      setSelectedNode(null);
      setFormData({ title: '', text: '' });
      setErrors({});
      setPendingParentId(parentId);
      setPendingPositionIsManual(false);
      setPendingPosition(defaultPosition);
      setIsModalOpen(true);
    },
    [nodes]
  );

  // Drag-from-node placement: remember which node/handle a connection drag
  // started from.
  const onConnectStart = useCallback((event, params) => {
    connectingNodeRef.current = params?.handleType === 'source' ? params.nodeId : null;
  }, []);

  // If the drag that started on a "+" handle is released over empty canvas
  // (rather than on another node's handle), treat it as "create a node here
  // and connect it to where I dragged from" — one action instead of two.
  const onConnectEnd = useCallback((event) => {
    const parentId = connectingNodeRef.current;
    connectingNodeRef.current = null;
    if (!parentId) return;

    const droppedOnPane = event.target?.classList?.contains('react-flow__pane');
    if (!droppedOnPane) return; // dropped on a handle -> a normal edge connection was made
    if (!reactFlowInstanceRef.current || !reactFlowWrapperRef.current) return;

    const clientX = event.clientX ?? event.changedTouches?.[0]?.clientX;
    const clientY = event.clientY ?? event.changedTouches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;

    const bounds = reactFlowWrapperRef.current.getBoundingClientRect();
    const flowPosition = reactFlowInstanceRef.current.project({
      x: clientX - bounds.left,
      y: clientY - bounds.top,
    });

    setPendingPosition(flowPosition);
    setPendingParentId(parentId);
    // The user explicitly chose this spot by dragging — don't auto-recenter
    // it (or its siblings) when the node is created.
    setPendingPositionIsManual(true);
    setIsEditMode(false);
    setSelectedNode(null);
    setFormData({ title: '', text: '' });
    setErrors({});
    setIsModalOpen(true);
  }, []);

  // Click a connection line to remove it (edges have no visible "x" of their
  // own, and relying on select-then-press-Backspace isn't discoverable, so we
  // surface the same delete-confirmation modal used for nodes).
  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setActions({
      open: true,
      data: { edgeId: edge.id },
      Header: 'Delete Connection',
      Body: 'Are you sure you want to remove this connection?',
    });
  }, []);

  // Auto-layout logic
  const autoLayout = (node, depth = 0, xOffset = 0, positions = new Map()) => {
    let width = 0;
    if (!node.children || node.children.length === 0) {
      width = 1;
    } else {
      node.children.forEach((child) => {
        const childWidth = autoLayout(child, depth + 1, xOffset + width, positions);
        width += childWidth;
      });
    }
    const x = (xOffset + width / 2) * HORIZONTAL_GAP;
    const y = depth * VERTICAL_GAP;
    positions.set(node._id, { x, y });
    return width;
  };

  const buildFlowAuto = (root) => {
    const positions = new Map();
    autoLayout(root, 0, 0, positions);

    const flowNodes = [];
    const flowEdges = [];

    const traverse = (node, parentId = null) => {
      flowNodes.push({
        id: node._id,
        type: 'custom',
        position: positions.get(node._id),
        data: {
          title: node.title,
          description: node.description,
        },
      });

      if (parentId) {
        flowEdges.push({
          id: `${parentId}-${node._id}`,
          source: parentId,
          target: node._id,
          type: 'smoothstep',
        });
      }

      node.children?.forEach((child) => traverse(child, node._id));
    };

    traverse(root);
    return { nodes: flowNodes, edges: flowEdges };
  };

  useEffect(() => {
    if (mindMapData?.data?.data) {
      const { nodes: flowNodes, edges: flowEdges } = buildFlowAuto(mindMapData.data.data);
      setNodes(flowNodes);
      setEdges(flowEdges);
    }
  }, [mindMapData]);

  const nodesWithHandlers = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onEdit: () => handleEditNodeById(node.id),
          onAddChild: () => openAddChildModal(node.id),
          onDelete: () =>
            setActions({
              open: true,
              data: { id: node.id },
              Header: 'Delete Node',
              Body: 'Are you sure you want to delete this node?',
            }),
        },
      })),
    [nodes, handleEditNodeById, openAddChildModal]
  );

  // Convert nodes + edges back to tree for saving
  const convertNodesEdgesToTree = () => {
    const nodeMap = {};
    nodes.forEach((n) => {
      nodeMap[n.id] = {
        _id: n.id,
        title: n.data.title,
        text: n.data.description,
        children: [],
      };
    });
    edges.forEach((e) => {
      if (nodeMap[e.source] && nodeMap[e.target]) {
        nodeMap[e.source].children.push(nodeMap[e.target]);
      }
    });

    // Reflect any manual rearrangement: order each parent's children
    // left-to-right by their current on-canvas x position instead of the
    // order the connecting edges happen to have been created in. Without
    // this, dragging a node to a new spot and saving would silently revert
    // to the original creation order.
    const positionById = new Map(nodes.map((n) => [n.id, n.position]));
    Object.values(nodeMap).forEach((node) => {
      node.children.sort((a, b) => {
        const xA = positionById.get(a._id)?.x ?? 0;
        const xB = positionById.get(b._id)?.x ?? 0;
        return xA - xB;
      });
    });

    const childIds = edges.map((e) => e.target);
    const root = nodes.find((n) => !childIds.includes(n.id));
    return root ? nodeMap[root.id] : null;
  };

  const saveMindmap = () => {
    const tree = convertNodesEdgesToTree();
    saveMindMapMutation(
      { tree },
      {
        onSuccess: (response) =>
          notifier.success(response?.data?.data?.message || response?.data?.message),
        onError: (failData) => {
          const errorData = failData?.response?.data;
          notifier.error(errorData?.message);
        },
      }
    );
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleDeleteConfirm}
      />
      <div style={{ padding: 10 }} className="d-flex justify-content-end">
        <AppButton
          variantClass="btn-primary mx-3"
          style={{ minWidth: '1.5rem' }}
          onClick={openAddNodeModal}
        >
          Add Node
        </AppButton>
        <AppButton
          variantClass="btn-primary mx-3"
          style={{ minWidth: '1.5rem' }}
          onClick={saveMindmap}
        >
          Save
        </AppButton>
      </div>

      {/* Signals that connection lines are clickable (to delete them) */}
      <style>{`
        .react-flow__edge {
          cursor: pointer;
        }
        .react-flow__edge-path {
          cursor: pointer;
        }
        .react-flow__edge:hover .react-flow__edge-path {
          stroke: #d32b82;
          stroke-width: 2.5;
        }
      `}</style>

      <div ref={reactFlowWrapperRef} style={{ height: '80vh', position: 'relative' }}>
        <ReactFlow
          nodes={nodesWithHandlers}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          defaultEdgeOptions={{ type: 'smoothstep' }}
          minZoom={0.4}
          maxZoom={4}
          onInit={(instance) => {
            reactFlowInstanceRef.current = instance;
            if (nodes.length > 0) {
              setTimeout(() => {
                instance.fitView({
                  padding: 0.1,
                  maxZoom: 1,
                });
                const { x, y, zoom } = instance.getViewport();
                instance.setViewport({
                  x,
                  y: y - 60,
                  zoom,
                });
              }, 50);
            }
          }}
        >
          <MiniMap />
          <Controls />
        </ReactFlow>
      </div>

      {/* Modal */}
      {isModalOpen &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
            }}
          >
            <div style={{ background: '#fff', padding: 20, borderRadius: 10, width: 800 }}>
              <AppTextField
                labelProps={{ label: 'Mind Map Title *', htmlFor: 'mindMapTitle' }}
                inputProps={{
                  id: 'mindMapTitle',
                  placeholder: 'Enter title',
                  value: formData.title,
                  onChange: (e) => {
                    const value = e.target.value;

                    setFormData((prev) => ({
                      ...prev,
                      title: value,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      title: '',
                    }));
                  },
                }}
              />
              {errors.title && (
                <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errors.title}</div>
              )}
              {/* <div style={{ marginTop: 16 }}>
                <AppTextArea
                  label="Mind Map text"
                  areaProps={{
                    placeholder: 'Enter text',
                    value: formData.text,
                    onChange: (e) => setFormData({ ...formData, text: e.target.value }),
                    rows: 6,
                  }}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <AppFilePicker
                  referenceKey="MINDMAP_IMAGE"
                  isPublic={true}
                  text="Upload Image"
                  handleChange={({ meta }) => {
                    console.log('Uploaded Meta:', meta);
                    const relativePath = meta.file;
                    const fullUrl = meta.basePath + meta.file;

                    setFormData((prev) => ({
                      ...prev,
                      image: relativePath, // store only path
                      previewImage: fullUrl, // temp preview
                    }));
                  }}
                />
              </div>
              {formData.image && (
                <img
                  src={formData.previewImage}
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    marginTop: 10,
                    borderRadius: 6,
                  }}
                />
              )} */}
              <div style={{ marginTop: 16 }}>
                <Editor
                  heading="Mind Map Content *"
                  data={{
                    value: formData.text || '',
                    onChange: (val) => {
                      setFormData((prev) => ({
                        ...prev,
                        text: val,
                      }));

                      setErrors((prev) => ({
                        ...prev,
                        text: '',
                      }));
                    },
                  }}
                />
              </div>

              {errors.content && (
                <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{errors.content}</div>
              )}
              <div className="d-flex justify-content-end mt-3">
                <AppButton
                  variantClass="btn-primary mx-3"
                  style={{ minWidth: '1.5rem' }}
                  onClick={handleAddNode}
                >
                  {isEditMode ? 'Update' : 'Add'}
                </AppButton>
                <AppButton
                  variantClass="btn-plane p--tint mx-3"
                  style={{ minWidth: '1.5rem' }}
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsEditMode(false);
                    setSelectedNode(null);
                    setFormData({ title: '', text: '' });
                    setErrors({});
                    setPendingPosition(null);
                    setPendingParentId(null);
                    setPendingPositionIsManual(false);
                  }}
                >
                  Cancel
                </AppButton>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

const mindMapSchema = Joi.object({
  title: Joi.string().min(1).max(100).trim().required().messages({
    'string.empty': 'Title is required',
    'string.max': 'Title must be less than 100 characters',
  }),
});
